/* eslint-disable max-lines-per-function */
import { ReactElement, useCallback, useState } from 'react';

import { PatientMedicine, PatientMedicineQuery } from '@dreryk/egabinet-prescription-frontend-api';
import {
   VisitDraftPrescriptionsQuantityQuery,
   VisitPrescriptionCopyCommand,
   VisitPrescriptionCopyResult,
   VisitPrescriptionPackageQuery,
} from '@dreryk/egabinet-visit-frontend-api';
import { VisitId } from '@dreryk/egabinet-visit-published-language-api';

import { performCqrsQuerySearch, refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { createSingleInterpolatedTranslationKey, PromiseVoidFunction } from '@dreryk/react-common';
import { Slot } from '@dreryk/watermelon';

import { VisitDiagnoses } from '~libs.shared.visit';

import { historicalVisitTranslationKeys } from '../../../translations';

import { CopiedPrescription, NotCopiedPrescriptionSummaryItem } from './copyPrescription.types';
import { buildNotCopiedSummaryItems } from './copyPrescription.utils';
import { CopySummaryModal } from './copySummary/CopySummaryModal';
import { NotCopiedPrescriptionReplacementModal } from './notCopiedPrescriptionReplacement/NotCopiedPrescriptionReplacementModal';

const t = historicalVisitTranslationKeys.copyPrescription;

type CopyPrescriptionPackageActionRenderProps = {
   copyPrescriptionPackage: PromiseVoidFunction;
};

type CopyPrescriptionPackageActionProps = {
   readonly children: (renderProps: CopyPrescriptionPackageActionRenderProps) => ReactElement;
   readonly prescriptionIds: number[];
   readonly targetVisitId: number;
   readonly patientId: number;
   readonly visitDiagnoses: VisitDiagnoses;
};

type CopyResult = {
   readonly copiedPrescriptions: CopiedPrescription[];
   readonly notCopiedSummaryItems: NotCopiedPrescriptionSummaryItem[];
};

const fetchMedicineNames = async (medicineIds: number[], patientId: number): Promise<ReadonlyMap<number, string>> => {
   const names = new Map<number, string>();

   const results = await Promise.all(
      medicineIds.map(async (medicineId) =>
         performCqrsQuerySearch<PatientMedicine>(() => {
            const query = new PatientMedicineQuery();
            query.patientId = patientId;
            query.medicineId = Slot.of(medicineId);
            return query;
         })
      )
   );

   for (let i = 0; i < medicineIds.length; i++) {
      const medicines = results[i];
      if (medicines.length > 0) {
         names.set(medicineIds[i], medicines[0].medicine.fullName);
      }
   }

   return names;
};

export const CopyPrescriptionPackageAction = (props: CopyPrescriptionPackageActionProps): ReactElement => {
   const [copyResult, setCopyResult] = useState<CopyResult | null>(null);
   const [replacingItem, setReplacingItem] = useState<NotCopiedPrescriptionSummaryItem | null>(null);

   const { executeAsync } = useCqrsCommand<VisitPrescriptionCopyResult, void>(() => {
      const command = new VisitPrescriptionCopyCommand();
      command.prescriptionIds = props.prescriptionIds;
      command.targetVisitId = VisitId.of(props.targetVisitId);
      return command;
   });

   const copyPrescriptionPackage = useCallback(async (): Promise<void> => {
      try {
         const result = await executeAsync();

         const notCopiedPrescriptions = result.notCopiedPrescriptions ?? [];
         const copiedPrescriptions = result.copiedPrescriptions ?? [];

         if (notCopiedPrescriptions.length === 0) {
            const medicineNames = copiedPrescriptions.map((item) => item.medicineName).join(', ');
            dispatchSnackbarPositiveEvent(
               createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.medicinesCopied,
                  'medicineNames',
                  medicineNames
               )
            );
            refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery);
            refetchCqrsQuery(VisitPrescriptionPackageQuery);
            return;
         }

         const uniqueMedicineIds = [...new Set(notCopiedPrescriptions.map((entry) => entry.medicineId))];
         const medicineNames =
            uniqueMedicineIds.length > 0
               ? await fetchMedicineNames(uniqueMedicineIds, props.patientId)
               : new Map<number, string>();

         const notCopiedSummaryItems = buildNotCopiedSummaryItems(notCopiedPrescriptions, medicineNames);

         setCopyResult({
            copiedPrescriptions: copiedPrescriptions,
            notCopiedSummaryItems: notCopiedSummaryItems,
         });
      } catch {
         dispatchSnackbarNegativeEvent(t.copyError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [executeAsync, props.patientId]);

   const handleReplace = useCallback((item: NotCopiedPrescriptionSummaryItem): void => {
      setReplacingItem(item);
   }, []);

   const handleReplacementSuccess = useCallback((): void => {
      setReplacingItem((currentReplacingItem) => {
         if (currentReplacingItem) {
            setCopyResult((current) => {
               if (!current) {
                  return current;
               }
               return {
                  ...current,
                  notCopiedSummaryItems: current.notCopiedSummaryItems.map((item) =>
                     item.medicineId === currentReplacingItem.medicineId ? { ...item, replaced: true } : item
                  ),
               };
            });
         }
         return null;
      });
   }, []);

   const handleReplacementCancel = useCallback((): void => {
      setReplacingItem(null);
   }, []);

   const handleClose = useCallback((): void => {
      refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery);
      refetchCqrsQuery(VisitPrescriptionPackageQuery);
      setCopyResult(null);
   }, []);

   return (
      <>
         {props.children({ copyPrescriptionPackage: copyPrescriptionPackage })}
         {copyResult !== null && replacingItem === null && (
            <CopySummaryModal
               copiedPrescriptions={copyResult.copiedPrescriptions}
               notCopiedSummaryItems={copyResult.notCopiedSummaryItems}
               onClose={handleClose}
               onReplace={handleReplace}
            />
         )}
         {replacingItem !== null && (
            <NotCopiedPrescriptionReplacementModal
               medicineId={replacingItem.medicineId}
               count={replacingItem.count}
               patientId={props.patientId}
               targetVisitId={props.targetVisitId}
               visitDiagnoses={props.visitDiagnoses}
               onSuccess={handleReplacementSuccess}
               onCancel={handleReplacementCancel}
            />
         )}
      </>
   );
};
